import { X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { JogPanel } from '@shared/components/navigation/jog-panel';
import { isCloud } from '@shared/lib/constants';
import { robotId as urlRobotId } from '@shared/lib/config';
import { useTeleopUser } from '@/components/layout/dashboard-middle';
import { useCurrentRobotKey } from '@/hooks/useCurrentRobot';
import { useRobotStore } from '@/stores/useRobotStore';
import { useActionError } from '@/stores/useActionError';
import { clearEstop, emergencyStop, endTeleop, startTeleop, teleopJog } from '@/lib/navigation-api';

function CenterEstopButton() {
  const estopActive = useRobotStore((s) => s.estopActive);
  const common =
    'w-32 h-32 rounded-full border-8 shadow-2xl active:scale-95 transition-transform flex items-center justify-center text-white font-black tracking-widest select-none';
  if (estopActive) {
    return (
      <button
        onClick={() => clearEstop()}
        aria-label="Reset Emergency Stop"
        className={`${common} bg-orange-500 border-orange-700 shadow-orange-900/60 text-sm leading-tight`}
      >
        RESET<br />E-STOP
      </button>
    );
  }
  return (
    <button
      onClick={() => emergencyStop()}
      aria-label="Emergency Stop"
      className={`${common} bg-red-600 border-red-800 shadow-red-900/60 text-lg`}
    >
      STOP
    </button>
  );
}

interface Props {
  onClose: () => void;
}

const PROD_HOST = 'https://dashboard.vernerobotics.com';

function QrJoystickPage({ onClose }: { onClose: () => void }) {
  // urlRobotId is parsed from window.location.search at module load — no
  // store reads, no API roundtrip — and covers the cloud-style dev URL
  // (?robot_id=xxx). The robot kiosk hits /kiosk/ without that param and
  // gets its identity from /kiosk/info via the fleet store, so we fall
  // back to that.
  const fleetRobotId = useCurrentRobotKey();
  const robotId = urlRobotId ?? fleetRobotId;
  const url = robotId
    ? `${PROD_HOST}/?robot_id=${encodeURIComponent(robotId)}#/joystick`
    : null;

  return (
    <div className="fixed inset-0 z-[65] bg-background flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <span className="text-sm font-semibold text-foreground">Joystick</span>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-muted hover:text-foreground hover:bg-surface-raised transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-6 p-6">
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-foreground mb-2">Scan to control on phone</h2>
          <p className="text-sm text-muted leading-relaxed">
            The joystick is disabled in local development. Scan this QR code on a phone to
            open the joystick against this robot in production.
          </p>
        </div>
        {url ? (
          <>
            <div className="bg-white p-4 rounded-2xl shadow-lg">
              <QRCodeSVG value={url} size={240} level="M" />
            </div>
            <code className="text-xs text-muted break-all max-w-md text-center px-4">{url}</code>
          </>
        ) : (
          <div className="text-sm text-muted text-center max-w-md">
            Add <code className="font-mono text-foreground">?robot_id=&lt;id&gt;</code> to your dev URL to generate a QR code.
          </div>
        )}
      </div>
    </div>
  );
}

export function MobileJoystickOverlay({ onClose }: Props) {
  const navStatus = useRobotStore((s) => s.navStatus);
  const baseOwner = useRobotStore((s) => s.baseOwner);
  const teleopUser = useRobotStore((s) => s.teleopUser);
  const currentUser = useTeleopUser();
  const runAction = useActionError((s) => s.runAction);

  const activeWaypoint = navStatus != null && ['pending', 'active', 'cancelling'].includes(navStatus);

  if (!isCloud) {
    return <QrJoystickPage onClose={onClose} />;
  }

  return (
    <div className="fixed inset-0 z-[65] bg-black">
      <JogPanel
        variant="mobile"
        onClose={onClose}
        baseOwner={baseOwner}
        teleopUser={teleopUser}
        currentUser={currentUser}
        activeWaypoint={activeWaypoint}
        onJog={(vx, vy, vw) => {
          teleopJog(currentUser, vx, vy, vw).catch(() => {});
        }}
        onToggleTeleop={(overridden) => {
          runAction(() => (overridden ? endTeleop(currentUser) : startTeleop(currentUser)));
        }}
      />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
        <CenterEstopButton />
      </div>
    </div>
  );
}
